import { collection, addDoc, getDocs, Timestamp, updateDoc, deleteDoc, doc, query, where, orderBy, limit } from 'firebase/firestore';
import { firestore } from './config';
import type { Category, Question } from '../types/game';

export type { Question } from '../types/game';

export const questionService = {
  async addQuestion(data: Omit<Question, 'createdAt' | 'updatedAt' | 'id'>) {
    const now = Timestamp.now();
    const questionData = {
      ...data,
      usageCount: 0,
      rating: 0,
      ratingCount: 0,
      createdAt: now,
      updatedAt: now,
    };
    const docRef = await addDoc(collection(firestore, 'questions'), questionData);
    return { id: docRef.id, ...questionData };
  },

  async getQuestions() {
    const snapshot = await getDocs(collection(firestore, 'questions'));
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Question[];
  },

  async getFinalJeopardyQuestions() {
    const q = query(collection(firestore, 'questions'), where('isFinalJeopardy', '==', true));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() })) as Question[];
  },

  async updateQuestion(id: string, data: Partial<Question>) {
    const docRef = doc(firestore, 'questions', id);
    const updateData = {
      ...data,
      updatedAt: Timestamp.now(),
    };
    await updateDoc(docRef, updateData);
    return { id, ...updateData };
  },

  async deleteQuestion(id: string) {
    await deleteDoc(doc(firestore, 'questions', id));
  },

  async getCategories() {
    const snapshot = await getDocs(collection(firestore, 'categories'));
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name,
        description: data.description || '',
        isActive: data.isActive,
      } as Category;
    }) as Category[];
  },

  async addCategory(data: Omit<Category, 'id'>) {
    const now = Timestamp.now();
    await addDoc(collection(firestore, 'categories'), {
      ...data,
      createdAt: now,
      updatedAt: now,
    });
  },

  async updateCategory(id: string, data: Partial<Omit<Category, 'id'>>) {
    const now = Timestamp.now();
    await updateDoc(doc(firestore, 'categories', id), {
      ...data,
      updatedAt: now,
    });
  },

  async deleteCategory(id: string) {
    // First delete all questions in this category
    const q = query(collection(firestore, 'questions'), where('categoryId', '==', id));
    const snapshot = await getDocs(q);
    const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
    
    // Then delete the category
    await deleteDoc(doc(firestore, 'categories', id));
  },

  async getActiveQuestions() {
    const q = query(
      collection(firestore, 'questions'),
      where('isActive', '!=', false)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Question[];
  },

  async getQuestionsByCategory(categoryId: string) {
    const q = query(
      collection(firestore, 'questions'),
      where('categoryId', '==', categoryId),
      where('isActive', '!=', false)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Question[];
  },

  async getRandomQuestions(categoryId: string, count: number) {
    const q = query(
      collection(firestore, 'questions'),
      where('categoryId', '==', categoryId),
      where('isActive', '!=', false),
      orderBy('usageCount', 'asc'),
      limit(count * 2) // Get more than needed to ensure we have enough after filtering
    );
    const snapshot = await getDocs(q);
    const questions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Question[];

    // Randomly select questions
    const selected = questions
      .sort(() => Math.random() - 0.5)
      .slice(0, count);

    // Update usage count for selected questions
    await Promise.all(
      selected.map(async (question) => {
        if (question.id) {
          await this.updateQuestion(question.id, {
            usageCount: (question.usageCount || 0) + 1,
            lastUsedAt: new Date(),
          });
        }
      })
    );

    return selected;
  },

  async rateQuestion(id: string, rating: number) {
    const docRef = doc(firestore, 'questions', id);
    const question = (await getDocs(query(collection(firestore, 'questions'), where('__name__', '==', id)))).docs[0].data() as Question;
    
    const newRatingCount = (question.ratingCount || 0) + 1;
    const newRating = ((question.rating || 0) * (question.ratingCount || 0) + rating) / newRatingCount;

    await updateDoc(docRef, {
      rating: newRating,
      ratingCount: newRatingCount,
      updatedAt: Timestamp.now(),
    });

    return { id, rating: newRating, ratingCount: newRatingCount };
  },

  async updateQuestionStats(
    questionId: string,
    isCorrect: boolean,
    responseTime: number
  ) {
    const docRef = doc(firestore, 'questions', questionId);
    const question = (await getDocs(query(collection(firestore, 'questions'), where('__name__', '==', questionId)))).docs[0].data() as Question;
    
    const correctAnswers = (question.correctAnswers || 0) + (isCorrect ? 1 : 0);
    const incorrectAnswers = (question.incorrectAnswers || 0) + (isCorrect ? 0 : 1);
    const totalAnswers = correctAnswers + incorrectAnswers;
    const successRate = (correctAnswers / totalAnswers) * 100;
    
    // Calculate new average response time
    const currentAvgTime = question.averageResponseTime || 0;
    const currentTotalTime = currentAvgTime * (totalAnswers - 1);
    const newAvgTime = (currentTotalTime + responseTime) / totalAnswers;

    await updateDoc(docRef, {
      correctAnswers,
      incorrectAnswers,
      successRate,
      averageResponseTime: newAvgTime,
      lastAnsweredAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    return {
      id: questionId,
      correctAnswers,
      incorrectAnswers,
      successRate,
      averageResponseTime: newAvgTime,
    };
  },
}; 